/** Copy for the members and attendance areas. English only; one strings file per area keeps a later i18n pass mechanical. */
export const t = {
  list: {
    title: "Members",
    intro: "Find anyone by phone number or the start of their name.",
    searchLabel: "Search members",
    searchPlaceholder: "Phone number or name",
    add: "Add member",
    closure: "Gym closure",
    filters: { all: "All", active: "Active", expiring: "Expiring", frozen: "Frozen", expired: "Expired" },
    filterLabel: "Show members",
    columns: { name: "Member", phone: "Phone", branch: "Home branch", plan: "Current plan", status: "Status", due: "Balance due", pick: "Select" },
    emptyTitle: "No members yet",
    emptyBody: "Add your first member to see them here.",
    noMatchTitle: "No members match",
    noMatchBody: "Try a different phone number or name, or clear the filter.",
    errorTitle: "Could not load members",
    summary: (n: number, more: boolean) => `${n}${more ? "+" : ""} ${n === 1 ? "member" : "members"} shown`,
    selected: (n: number) => `${n} selected`,
    freezeSelected: "Freeze selected",
  },
  status: { active: "Active", expiring: "Expiring", expired: "Expired", frozen: "Frozen", upcoming: "Starts soon", none: "No plan", dues: "Dues" },
  add: {
    title: "Add member",
    intro: "A member with this phone number is opened instead of added twice.",
    name: "Full name", phone: "Mobile number", email: "Email (optional)", branch: "Home branch", save: "Add member", saving: "Adding…",
    existing: "This number already belongs to a member. Opened their profile.",
    created: "Member added",
    nameRequired: "Enter a name", phoneRequired: "Enter a mobile number", emailInvalid: "Enter a valid email", branchRequired: "Choose a branch",
    noBranches: "No branch available to add a member to.",
  },
  profile: {
    back: "← Members",
    tabs: { overview: "Overview", plans: "Plans", checkins: "Check-ins", bills: "Bills" },
    goodToTrain: "Good to train",
    notGood: "Not good to train",
    errorTitle: "Could not load this member",
    notFoundTitle: "Member not found",
    notFoundBody: "This member does not exist, or is not in this gym.",
    details: "Details", edit: "Edit details", phone: "Phone", email: "Email", branch: "Home branch", owner: "Looked after by", joined: "Member since",
    balance: "Balance due", wallet: "Wallet", packs: "PT packs", noPacks: "No PT packs.",
    packLeft: (r: number, t: number) => `${r} of ${t} sessions left`,
    packBooked: (n: number) => `${n} booked`,
    plans: "Plans", allPlans: "All plans", noPlans: "No plans yet", noPlansBody: "Sell this member a plan at the counter.",
    plansError: "Could not load plans",
    actions: { collect: "Collect payment", renew: "Renew", freeze: "Freeze", extend: "Extend", pt: "Book PT", code: "Give login code", unfreeze: "Unfreeze", transfer: "Transfer", cancel: "Cancel plan" },
    checkins: "Check-ins", noCheckins: "No check-ins yet", noCheckinsBody: "Visits show here once this member scans in.",
    bills: "Bills", noBills: "No bills yet", noBillsBody: "Bills from the counter show here.", open: "Open", billsError: "Could not load bills",
    notAllowedBills: "Bills are visible to roles with billing access.",
  },
  edit: { title: "Edit details", name: "Full name", email: "Email", branch: "Home branch", save: "Save changes", saving: "Saving…", saved: "Details saved", phoneNote: "The phone number cannot be changed here." },
  drawer: {
    close: "Close", confirm: "Confirm",
    working: "Working…",
    reason: "Reason", reasonRequired: "Give a reason",
    dateRequired: "Choose a date",
    extend: { title: "Extend plan", intro: "Adds free days at the end of the plan. No money moves.", days: "Days to add", daysInvalid: "Enter a whole number of days from 1 to 365", confirm: "Extend plan", done: (d: string) => `Extended. New end date ${d}.` },
    freeze: {
      title: "Freeze plan", intro: "Pauses the plan between two dates and moves the end date out by the days frozen.", from: "Freeze from", to: "Freeze to", confirm: "Freeze plan",
      done: (days: number, end: string) => `Frozen for ${days} ${days === 1 ? "day" : "days"}. New end date ${end}.`,
      fee: "Freeze fee", feeCharge: "Charge the freeze fee at the counter", feeWaive: "Waive the fee and freeze now",
      feeConfigured: "This gym charges a freeze fee. A fee is a bill, so it is taken at the counter.",
      feeUnknown: "A freeze fee may apply. You cannot see the fee setting, so choose how to handle it.",
      toCounter: "Open the counter", order: "The end date cannot be before the start date.",
    },
    unfreeze: { title: "Unfreeze plan", intro: "Ends the freeze early. Unused frozen days come off the end date.", on: "Resume on", which: "Freeze to end", confirm: "Unfreeze plan", done: (d: string) => `Unfrozen. New end date ${d}.`, none: "There is no open freeze on this plan." },
    transfer: {
      title: "Transfer plan", intro: "Moves the days left on this plan to another member. The original bill stays as it is.", to: "Transfer to", search: "Search by phone or name", asOf: "Transfer on", confirm: "Transfer plan",
      pick: "Choose a member", noMatch: "No member found", done: "Plan transferred.", fee: "Charge a transfer fee at the counter instead", self: "Choose a different member from the one who owns the plan.",
    },
    cancel: {
      title: "Cancel plan", intro: "Ends the plan and settles the unused part.", asOf: "Cancel on", settlement: "Unused part",
      reduce: "Take it off what the member owes", credit: "Keep it as wallet credit", refund: "Refund it (opens the counter)",
      confirm: "Cancel plan", done: "Plan cancelled.", toCounter: "Open the counter",
    },
    code: {
      title: "Give a login code", intro: "A one-time code so this member can sign in without their phone. Read it to them; it is not sent anywhere.",
      issue: "Create code", code: "Code", expires: (t: string) => `Valid until ${t}`, masked: "The member's phone number is hidden for your role, so a code cannot be made.",
    },
    bulk: {
      title: "Gym closure", intro: "Freeze plans together for the days the gym is shut. No fee is charged.", branch: "Branch", from: "Closed from", to: "Closed to", reason: "Reason", confirm: "Freeze plans",
      scopeAll: "Every active plan running at this branch on the first day",
      scopePicked: (n: number) => `Only the ${n} plans selected in the list`,
      scope: "Plans",
      result: (a: number, f: number) => `${a} frozen, ${f} not frozen.`, failedHeading: "Not frozen", appliedHeading: "Frozen",
      noPicked: "Select members in the list first.",
    },
    stale: "This plan changed while you were looking at it. Close this and try again.",
  },
  attendance: {
    title: "Attendance", intro: "Who came in, and who has gone quiet.",
    tabs: { checkins: "Check-ins", atRisk: "At risk" },
    date: "Day", pickDay: "Choose a day", decision: "Show", all: "All scans", allowed: "Allowed", denied: "Denied",
    columns: { time: "Time", member: "Member", result: "Result", why: "Why", counted: "Counts as visit" },
    emptyCheckins: "No check-ins on this day", emptyCheckinsBody: "Scans from the door and the QR show here.",
    checkinsError: "Could not load check-ins",
    counted: "Yes", notCounted: "No",
    allow: "Allowed", deny: "Denied",
    level: "Level", watch: "Watch", atRisk: "At risk", levels: { watch: "Watch and at risk", at_risk: "At risk only" },
    riskColumns: { member: "Member", level: "Level", why: "Why" },
    emptyRisk: "Nobody at risk", emptyRiskBody: "Everyone is visiting as usual.", riskError: "Could not load the at-risk list",
    summary: (n: number, more: boolean) => `${n}${more ? "+" : ""} shown`,
  },
  problem: { retry: "Retry", generic: "Something went wrong. Try again." },
};

export const DENY_REASON: Record<string, string> = {
  outside_hours: "Outside opening hours",
  no_active_plan: "No active plan",
  plan_expired: "Plan expired",
  plan_frozen: "Plan frozen",
  plan_not_started: "Plan not started",
  wrong_branch: "Plan not valid at this branch",
  dues_blocked: "Unpaid dues",
  duplicate: "Already checked in",
  daily_limit: "Daily visit limit reached",
};

export const reasonText = (code: string) => DENY_REASON[code] ?? code.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
